import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageContainer, PageContent } from '@/components/ui/page-layout';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import CollaborationGraphModal from '../components/CollaborationGraphModal';
import PersonHero from '../components/person/PersonHero';
import CompetencyRow from '../components/person/CompetencyRow';
import CompetencyRowSkeleton from '../components/person/CompetencyRowSkeleton';
import OutcomesByYearChart from '../components/person/OutcomesByYearChart';
import OutcomeTypesChart from '../components/person/OutcomeTypesChart';
import EntryListCard from '../components/person/EntryListCard';
import { isPracticalType } from '../utils/practicalResults';
import {
  getPersonDetails,
  getPersonCompetencies,
  getPersonProjects,
  getProjectsList,
  getPersonCollaborationGraph,
  getPersonCollaborators,
} from '../services/apiService';
import { Collaborator, CollaborationGraphResponse, CompetencyResult } from '../types/api';

interface PersonData {
  name: string;
  department: string;
  faculty: string;
}

interface ProjectItem {
  id: number;
  name: string;
  type: string;
  year: number;
  description: string;
  is_practical?: boolean;
}

interface ProjectListItem {
  id?: number;
  name: string;
  type: string;
  year: number;
  description: string;
}

export default function PersonPage() {
  const { t } = useTranslation('person');
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [person, setPerson] = useState<PersonData | null>(null);
  const [competencies, setCompetencies] = useState<(string | CompetencyResult)[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [compLoading, setCompLoading] = useState<boolean>(true);
  const [compError, setCompError] = useState<string | null>(null);

  const [projects, setProjects] = useState<ProjectItem[]>([]);
  const [projectsLoading, setProjectsLoading] = useState<boolean | null>(null);
  const [projectsError, setProjectsError] = useState<string | null>(null);

  const [projectsList, setProjectsList] = useState<ProjectListItem[]>([]);
  const [projectsListLoading, setProjectsListLoading] = useState<boolean | null>(null);
  const [projectsListError, setProjectsListError] = useState<string | null>(null);

  const [showCollaborationGraph, setShowCollaborationGraph] = useState<boolean>(false);
  const [graphData, setGraphData] = useState<CollaborationGraphResponse | null>(null);
  const [graphLoading, setGraphLoading] = useState<boolean>(false);
  const [graphError, setGraphError] = useState<string | null>(null);

  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [collaboratorsLoading, setCollaboratorsLoading] = useState<boolean>(true);
  const [collaboratorsError, setCollaboratorsError] = useState<string | null>(null);

  const hideBackButton = ['ctu-companies', 'modal'].includes(
    new URLSearchParams(location.search).get('from') || '',
  );

  const goBackToSearch = () => {
    if (location.state?.from) {
      navigate(location.state.from);
      return;
    }
    const savedCompetenciesState = sessionStorage.getItem('competenciesSearchState');
    const savedCompaniesState = sessionStorage.getItem('companiesSearchState');
    const savedMainSearchState = sessionStorage.getItem('mainSearchState');
    if (savedCompetenciesState) navigate(`/competencies?${savedCompetenciesState}`);
    else if (savedCompaniesState) navigate(`/companies?${savedCompaniesState}`);
    else if (savedMainSearchState) navigate(`/?${savedMainSearchState}`);
    else navigate('/people');
  };

  const handleNavigate = (path: string, e: React.MouseEvent) => {
    e.preventDefault();
    if (e.ctrlKey || e.metaKey) {
      window.open(path, '_blank');
    } else {
      navigate(path);
    }
  };

  const handleKeywordClick = (keyword: string, e: React.MouseEvent) => {
    handleNavigate(`/competencies/${encodeURIComponent(keyword)}`, e);
  };

  const fetchCollaborationGraph = async (personId?: string | number) => {
    const targetId = personId || id;
    if (!targetId) return;
    setGraphLoading(true);
    setGraphError(null);
    try {
      const data = await getPersonCollaborationGraph(targetId);
      setGraphData(data);
      setShowCollaborationGraph(true);
    } catch (err: any) {
      setGraphError(err.message || 'Failed to load collaboration graph');
    } finally {
      setGraphLoading(false);
    }
  };

  useEffect(() => {
    if (showCollaborationGraph && id) {
      fetchCollaborationGraph(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!id) return;

    const loadPersonDetails = async () => {
      setLoading(true);
      setError(null);
      setPerson(null);
      setCompetencies([]);
      setProjects([]);
      setProjectsList([]);
      setCompLoading(true);
      setProjectsLoading(true);
      setProjectsListLoading(true);
      setCompError(null);
      setProjectsError(null);
      setProjectsListError(null);

      try {
        const data = await getPersonDetails(id);
        if (data && data.results) {
          setPerson({
            name: data.results.name,
            department: data.results.department,
            faculty: data.results.faculty,
          });
        } else {
          throw new Error('Person data not found in API response.');
        }
      } catch (err: any) {
        setError(err.message || 'An unexpected error occurred while fetching person details.');
        setPerson(null);
      } finally {
        setLoading(false);
      }
    };
    loadPersonDetails();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setCompLoading(true);
      setCompError(null);
      try {
        const data = await getPersonCompetencies(id);
        setCompetencies(data.results || []);
      } catch (err: any) {
        setCompError(err.message || 'Failed to fetch competencies');
      } finally {
        setCompLoading(false);
      }
    };
    load();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setProjectsLoading(true);
      setProjectsError(null);
      try {
        const data = await getPersonProjects(id);
        setProjects(
          (data.results || []).map((project: any) => ({
            id: project.id,
            name: project.name,
            type: project.type,
            year: project.year,
            description: project.description,
            is_practical: project.is_practical,
          })),
        );
      } catch (err: any) {
        setProjectsError(err.message || 'Failed to fetch projects');
      } finally {
        setProjectsLoading(false);
      }
    };
    load();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setProjectsListLoading(true);
      setProjectsListError(null);
      try {
        const result = await getProjectsList(id);
        setProjectsList(result.results || []);
      } catch (err: any) {
        setProjectsListError(err.message || 'Failed to fetch projects list');
      } finally {
        setProjectsListLoading(false);
      }
    };
    load();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setCollaboratorsLoading(true);
      setCollaboratorsError(null);
      try {
        const data = await getPersonCollaborators(id);
        setCollaborators(data.results || []);
      } catch (err: any) {
        setCollaboratorsError(err.message || 'Failed to fetch collaborators');
      } finally {
        setCollaboratorsLoading(false);
      }
    };
    load();
  }, [id]);

  const practicalResultsCount = projects.filter(
    proj => proj.is_practical || isPracticalType(proj.type),
  ).length;

  const heroRef = useRef<HTMLDivElement>(null);
  const [heroHeight, setHeroHeight] = useState<number | null>(null);
  useEffect(() => {
    if (!heroRef.current || typeof ResizeObserver === 'undefined') return;
    const el = heroRef.current;
    const obs = new ResizeObserver(entries => {
      for (const entry of entries) {
        const h = Math.round(entry.contentRect.height);
        setHeroHeight(prev => (prev === h ? prev : h));
      }
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [person]);

  return (
    <PageContainer>
      <PageContent>
        {!hideBackButton && (
          <Button variant="ghost" size="sm" className="mb-4" onClick={goBackToSearch}>
            ← {t('backToSearch')}
          </Button>
        )}

        {loading && (
          <div
            className="flex flex-col gap-6 py-2"
            aria-busy="true"
            aria-label={t('loadingDetails')}
          >
            <Skeleton className="h-44 w-full rounded-lg" />
            <Skeleton className="h-72 w-full rounded-lg" />
            <Skeleton className="h-80 w-full rounded-lg" />
            <Skeleton className="h-16 w-full rounded-lg" />
            <Skeleton className="h-16 w-full rounded-lg" />
          </div>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {compError && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{compError}</AlertDescription>
          </Alert>
        )}

        {person && !loading && (
          <>
            <div ref={heroRef}>
              <PersonHero
                name={person.name}
                department={person.department}
                faculty={person.faculty}
                resultsCount={projects.length}
                practicalResultsCount={practicalResultsCount}
                projectsCount={projectsList.length}
                competenciesCount={competencies.length}
              />
            </div>

            <div style={heroHeight ? { height: heroHeight } : undefined} className="mb-6">
              <OutcomesByYearChart outcomes={projects} projects={projectsList} />
            </div>

            {compLoading ? (
              <CompetencyRowSkeleton />
            ) : (
              <CompetencyRow
                competencies={competencies}
                outcomesCount={projects.length}
                projectsCount={projectsList.length}
                onKeywordClick={handleKeywordClick}
                collaborators={collaborators}
                collaboratorsLoading={collaboratorsLoading}
                collaboratorsError={collaboratorsError}
                onCollaboratorClick={(personId, e) =>
                  handleNavigate(`/person/${personId}`, e)
                }
                onCollaboratorProjectClick={(projectId, e) =>
                  handleNavigate(`/outcome/${projectId}`, e)
                }
                onShowGraph={() => fetchCollaborationGraph()}
                graphLoading={graphLoading}
              />
            )}

            <OutcomeTypesChart outcomes={projects} />

            <EntryListCard
              items={projects}
              loading={projectsLoading}
              error={projectsError}
              onNavigate={handleNavigate}
              detailPathPrefix="/outcome"
              accentMode="practical"
              enablePracticalFilter
              labels={{
                section: t('sections.results'),
                showButton: t('buttons.showResults'),
                hideButton: t('buttons.hideResults'),
                filterPlaceholder: t('resultsSection.filterPlaceholder'),
                nameColumn: t('table.resultName'),
                typeColumn: t('table.type'),
                yearColumn: t('table.year'),
                noDescription: t('table.noDescription'),
                emptyTitle: t('resultsSection.noResults'),
                emptyDescription: t('resultsSection.emptyDescription'),
                noMatch: t('resultsSection.noMatch'),
                viewDetails: t('buttons.viewDetails'),
                practicalOnly: t('resultsSection.onlyPractical'),
              }}
            />

            <EntryListCard
              items={projectsList}
              loading={projectsListLoading}
              error={projectsListError}
              onNavigate={handleNavigate}
              detailPathPrefix="/outcome"
              accentMode="project"
              labels={{
                section: t('sections.projects'),
                showButton: t('buttons.showProjects'),
                hideButton: t('buttons.hideProjects'),
                filterPlaceholder: t('projectsSection.filterPlaceholder'),
                nameColumn: t('table.projectName'),
                typeColumn: t('table.type'),
                yearColumn: t('table.year'),
                noDescription: t('table.noDescription'),
                emptyTitle: t('projectsSection.noProjects'),
                emptyDescription: t('projectsSection.emptyDescription'),
                noMatch: t('projectsSection.noMatch'),
                viewDetails: t('buttons.viewDetails'),
              }}
            />

            {showCollaborationGraph && graphData && (
              <CollaborationGraphModal
                data={graphData}
                centerPersonName={person.name}
                onClose={() => setShowCollaborationGraph(false)}
                onNodeClick={(nodeId) => navigate(`/person/${nodeId}`)}
                onProjectClick={(projectId) => navigate(`/outcome/${projectId}`)}
              />
            )}

            {graphError && (
              <Alert variant="destructive" className="mt-5">
                <AlertDescription>{graphError}</AlertDescription>
              </Alert>
            )}
          </>
        )}
      </PageContent>
    </PageContainer>
  );
}
